import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { JSEncrypt } from 'jsencrypt';
import CurrentPassword from './Steps/CurrentPassword';
import { Button } from '../../elements';
import NewPassword from './Steps/NewPassword';
import ConfirmNewPassword from './Steps/ConfirmNewPassword';
import dashboardColors from '../../helpers/dashboardColors';
import { ToastCtx } from '~/providers/ToastProvider';
import ForgotCardPassword from './Steps/ForgotCardPassword';
import { cards } from '../../helpers/api';
import messages from '~/helpers/api/messages';
import { Loader } from '~/components';
import { setCreatingPassword } from '~/store/modules/cardPassword/actions';
import { JVCEF, VRPAT } from '~/config/constants';
import { setAxiosHeader } from '~/helpers/setAxiosHeader';
import CurrentCardNumber from './Steps/CurrentCardNumber';

const Container = styled.form`
  display: flex;
  align-items: center;
  background-color: ${({ theme, activeCardCode }) =>
    dashboardColors(theme, activeCardCode).bgModal};
  color: ${({ theme, activeCardCode }) =>
    dashboardColors(theme, activeCardCode).colorModal};
  padding: 15px 0;

  @media (min-width: ${({ theme }) => theme.devices.mobile}) {
    padding: 0;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 15px;

  button {
    width: 100%;
  }

  @media (min-width: ${({ theme }) => theme.devices.mobile}) {
    padding: 70px 0;

    * {
      max-width: 333px;
    }
  }
`;

export default function ChangeCardPassword({
  clearValues,
  currentStep,
  setStep,
}) {
  const { formState, getValues, errors, trigger, ...formControl } = useForm({
    mode: 'onChange',
  });
  const dispatch = useDispatch();
  const [isLoading, setIsloading] = useState(false);
  const [cardPassword, setCardPassword] = useState({});
  const [cardNewPassword, setCardNewPassword] = useState({});
  const [
    cardNewPasswordConfirmation,
    setCardNewPasswordConfirmation,
  ] = useState({});
  const [forceFocus, setForceFocus] = useState('');
  const toast = useContext(ToastCtx);
  const user = useSelector(state => state.user);
  const activeCard = useSelector(state => state.home.cards.active);
  const creatingPassword = useSelector(
    state => state.cardPassword.creatingPassword,
  );
  const issuer = useSelector(state => state?.issuer?.name);
  const btnStyle = issuer === VRPAT ? 'secondary' : 'caixa';
  const { codigoProduto, tipo } = activeCard;
  const color = issuer === JVCEF ? 'black' : 'white';

  function getObjData(obj) {
    let cont = 0;
    let value = '';
    // eslint-disable-next-line no-unused-vars
    Object.entries(obj).forEach(([key, val]) => {
      if (val) {
        cont += 1;
      }
      value = value.concat(val);
    });

    return { size: cont, value };
  }

  function clearAllValues() {
    setCardPassword({});
    setCardNewPassword({});
    setCardNewPasswordConfirmation({});
    formControl.setValue('cpf', '');
    formControl.setValue('cvv', '');
    formControl.setValue('cardNumberChangingPassword', '');
    formControl.setValue('dueDate', '');
    formControl.setValue('numberCard', '');
  }

  useEffect(() => {
    if (clearValues) {
      clearAllValues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearValues]);

  const passwordSize = getObjData(cardPassword).size;
  const passwordValue = getObjData(cardPassword).value;
  const newPasswordValue = getObjData(cardNewPassword).value;
  const newPasswordSize = getObjData(cardNewPassword).size;
  const newPasswordConfirmationValue = getObjData(cardNewPasswordConfirmation)
    .value;
  const newPasswordConfirmationSize = getObjData(cardNewPasswordConfirmation)
    .size;

  function validatePassword() {
    if (passwordSize === 4) {
      setStep('new_password');
    }
  }

  useEffect(() => {
    validatePassword();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardPassword]);

  function validateNewPassword() {
    setForceFocus('');

    if (newPasswordSize === 4) {
      const passwordEquality = passwordValue === newPasswordValue;
      const passwordEqualDigits = newPasswordValue
        .substring(0, 3)
        .split('')
        .every((digit, index) => {
          return Number(newPasswordValue[index + 1]) === Number(digit);
        });
      const passwordEqualCard =
        newPasswordValue === activeCard?.numeroCartao?.toString().slice(-4);

      if (passwordEquality) {
        // Volta que a nova senha precisa ser diferente da atual
        setCardNewPassword({});
        setForceFocus('digit_1');
        toast.dispatch(
          'Validação de senha',
          'error',
          <>&#128577; Nova senha deve ser diferente da atual.</>,
        );

        return;
      }

      if (passwordEqualDigits) {
        setCardNewPassword({});
        setForceFocus('digit_1');
        toast.dispatch(
          'Validação de senha',
          'error',
          <>&#128577; A nova senha não pode conter dígitos iguais.</>,
        );

        return;
      }

      if (passwordEqualCard) {
        setCardNewPassword({});
        setForceFocus('digit_1');
        toast.dispatch(
          'Validação de senha',
          'error',
          <>
            &#128577; A nova senha não pode ser igual aos 4 últimos dígitos do
            cartão.
          </>,
        );

        return;
      }

      setStep('new_password_confirmation');
    }
  }

  useEffect(() => {
    validateNewPassword();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardNewPassword]);

  async function getPublicKey(cpf) {
    let response;

    try {
      setIsloading(true);
      response = await cards.getPublicKey(cpf);
    } catch (error) {
      toast.dispatch(
        messages(error, 'public_key', <span>{error?.result}</span>),
      );
    } finally {
      setIsloading(false);
    }

    return response;
  }

  async function createCardPassword(payload, cpf) {
    try {
      setIsloading(true);
      const response = await cards.createCardPassword(payload, cpf);
      toast.dispatch(messages(response, 'create_card_password'));
      setStep('');
      dispatch(setCreatingPassword(false));
    } catch (error) {
      setStep('forgot_card_password');
      toast.dispatch(
        messages(error, 'create_card_password', <span>{error?.result}</span>),
      );
    } finally {
      setIsloading(false);
      clearAllValues();
    }
  }

  function encryptData(publicKey, data) {
    const jsEncrypt = new JSEncrypt();
    jsEncrypt.setPublicKey(publicKey);

    return jsEncrypt.encrypt(JSON.stringify(data));
  }

  async function createPassword() {
    const cpf = getValues('cpf');
    const formattedCpf = cpf.replace(/\D/g, '');
    const numberCard = getValues('cardNumberChangingPassword');
    const dueDate = getValues('dueDate');
    const cvv = getValues('cvv');

    const twoDigitYear = dueDate
      .getFullYear()
      .toString()
      .substr(-2);
    const twoDigitMonth = (dueDate.getMonth() + 1).toString().padStart(2, '0');

    const data = {
      numero_cartao: numberCard.split(' ').join(''),
      cvv,
      data_validade: `${twoDigitYear}${twoDigitMonth}`,
      nova_senha: newPasswordValue,
      confirmacao_senha: newPasswordConfirmationValue,
    };

    const response = await getPublicKey(formattedCpf);

    // eslint-disable-next-line camelcase
    const { id_chave, chave_publica } = response;

    setAxiosHeader([
      {
        name: 'id-key',
        value: id_chave,
      },
    ]);

    const encryptedData = encryptData(chave_publica, data);

    const payload = {
      encryptedData,
      ipAddress: '',
      origin: 'portal',
      requester: `nome: ${user?.name}, email: ${user?.email}`,
    };

    await createCardPassword(payload, formattedCpf);
  }

  async function changePassword() {
    const numberCard = getValues('cardNumberChangingPassword');
    const { cpf } = user;

    const payload = {
      cpf,
      numberCard: numberCard.split(' ').join(''),
      newPassword: newPasswordValue,
      confirmationNewPassword: newPasswordConfirmationValue,
      currentPassword: passwordValue,
    };

    try {
      setIsloading(true);
      const response = await cards.changeCardPassword(payload);
      toast.dispatch(messages(response, 'change_card_password'));
      setStep('');
    } catch (error) {
      setStep('password');
      toast.dispatch(
        messages(error, 'change_card_password', <span>{error?.result}</span>),
      );
    } finally {
      setIsloading(false);
      clearAllValues();
    }
  }

  async function validateNewPasswordConfirmation() {
    const valuesAreEqual = newPasswordValue === newPasswordConfirmationValue;
    let method = () => null;

    if (creatingPassword === true) {
      method = createPassword;
    } else if (creatingPassword === false) {
      method = changePassword;
    }

    if (newPasswordConfirmationSize === 4 && valuesAreEqual) {
      await method();
    }

    if (newPasswordConfirmationSize === 4 && !valuesAreEqual) {
      // Invalid
      setStep('new_password');
      setCardNewPassword({});
      setCardNewPasswordConfirmation({});
      toast.dispatch(
        '',
        'error',
        <>As senhas não conferem. Por favor, tente novamente</>,
      );
    }
  }

  useEffect(() => {
    validateNewPasswordConfirmation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardNewPasswordConfirmation]);

  function digitsOnChange(objValue, name) {
    const { key, value } = objValue;

    if (name === 'password') {
      setCardPassword({
        ...cardPassword,
        [key]: value,
      });
    }
    if (name === 'new_password') {
      setCardNewPassword({
        ...cardNewPassword,
        [key]: value,
      });
    }
    if (name === 'new_password_confirmation') {
      setCardNewPasswordConfirmation({
        ...cardNewPasswordConfirmation,
        [key]: value,
      });
    }
  }

  function handleCurrentStep() {
    let component;

    if (currentStep === 'password') {
      component = (
        <>
          <CurrentCardNumber
            activeCard={activeCard}
            formControl={formControl}
            errors={errors}
          />
        </>
      );
    }
    if (currentStep === 'password_second_step') {
      component = (
        <>
          <CurrentPassword
            onChange={digitsOnChange}
            values={cardPassword}
            color={color}
          />
        </>
      );
    }
    if (currentStep === 'new_password') {
      component = (
        <NewPassword
          forceFocus={forceFocus}
          onChange={digitsOnChange}
          values={cardNewPassword}
          color={color}
        />
      );
    }
    if (currentStep === 'new_password_confirmation') {
      component = (
        <ConfirmNewPassword
          onChange={digitsOnChange}
          values={cardNewPasswordConfirmation}
          color={color}
        />
      );
    }
    if (currentStep === 'forgot_card_password') {
      component = (
        <ForgotCardPassword
          formControl={formControl}
          errors={errors}
          trigger={trigger}
        />
      );
    }

    return component;
  }

  // eslint-disable-next-line no-unused-vars
  function handleButton() {
    let stepName = '';
    let label = '';
    let showButton = false;
    let ariaLabel = '';
    let dataTestid = '';

    if (currentStep === 'password') {
      stepName = 'password_second_step';
      label = 'Continuar';
      showButton = true;
      ariaLabel = 'Continuar';
      dataTestid = 'Continuar';
    }

    if (currentStep === 'password_second_step') {
      stepName = 'forgot_card_password';
      label = 'Não lembro a minha senha';
      showButton = true;
      ariaLabel = 'Não lembro a minha senha';
    }

    if (currentStep === 'forgot_card_password') {
      if (creatingPassword === false) {
        dispatch(setCreatingPassword(true));
      }

      stepName = 'new_password';
      label = 'Continuar';
      showButton = true;
      ariaLabel = 'Continuar';
    }

    return (
      showButton && (
        <Button
          disabled={!formState.isValid}
          onClick={() => setStep(stepName)}
          label={label}
          color={btnStyle}
          ariaLabel={ariaLabel}
          data-testid={dataTestid}
        />
      )
    );
  }

  return (
    <Container activeCardCode={tipo ? 'caixa' : codigoProduto}>
      <Loader isLoading={isLoading}>
        <Content>
          {handleCurrentStep()}
          {handleButton()}
        </Content>
      </Loader>
    </Container>
  );
}

ChangeCardPassword.propTypes = {
  currentStep: PropTypes.string,
  setStep: PropTypes.func,
  clearValues: PropTypes.bool,
};
